import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../hooks';
import { useField } from '@unform/core';
import { EstoqueOperacoesService } from '../services/api/estoque/estoqueOperacoes/EstoqueOperacoesService';

type TAutoCompleteOption = {
  idoperacoes: string;
  label: string;
  faturamento: boolean;
  tpag: number;
} 
interface IAutoCompleteOperacoesProps {
  isExternalLoading?: boolean;
  disabled?: boolean;
  faturamento?: (novoTextoselect: boolean| undefined) => void;
  venda?: (novoTextoselect: boolean| undefined) => void;
  operacao?: (novoTextoselect: string| undefined) => void;
  tpag?: (novoTextoselect: number| undefined) => void;
  // idoperacao da opção escolhida — diferente de `operacao` (que manda só o texto/descrição).
  // useField/unform guarda esse id internamente (`selectedId`), mas só fica visível pro
  // formulário pai de forma assíncrona (via formRef.getData(), depois do próximo render); quem
  // precisa reagir na hora à troca (ex.: decidir se a operação virou Orçamento ou não) usa isto.
  onIdOperacaoChange?: (novoId: string | undefined) => void;
  tipo: number;
  // Restringe as opções a essa lista de idoperacoes (ex.: tela de venda só quer Venda à
  // Vista/Prazo/Orçamento, não as outras operações que também têm tipo=1) — sem essa prop,
  // comportamento inalterado (mostra tudo que a API retornar), pros outros usos deste
  // componente (entrada de estoque, NFe de entrada etc.).
  apenasIds?: string[];
}
export const AutoCompleteOperacoes: React.FC<IAutoCompleteOperacoesProps> = ({ isExternalLoading = false, disabled = false, tipo, faturamento, operacao, tpag, apenasIds, onIdOperacaoChange}) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField('idoperacao');
  const { debounce } = useDebounce();
  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  // Só usado no modo apenasIds — a lista fixa completa (as N operações permitidas), buscada
  // uma vez por id. `opcoes` (acima) é o que a Autocomplete de fato exibe, já filtrado por
  // `busca` a partir daqui.
  const [opcoesFixas, setOpcoesFixas] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  useEffect(() => {
    // Lista restrita a um conjunto pequeno e fixo de ids — busca cada uma direto por id, uma
    // única vez, em vez de depender do endpoint paginado/genérico (`estoqueoperacoeslist`, que
    // ordena por descrição e corta em `limit` — com muitas operações cadastradas, itens que
    // começam com letras mais tarde no alfabeto, como "Venda...", ficam de fora da primeira
    // página quando a busca está vazia, mesmo estando entre os `apenasIds` pedidos).
    if (!apenasIds || apenasIds.length === 0) return;

    setIsLoading(true);
    Promise.all(apenasIds.map(id => EstoqueOperacoesService.getById(id)))
      .then((resultados) => {
        setIsLoading(false);
        const todasAsOpcoes = resultados
          .map((resultado, index) => (resultado instanceof Error ? null : {
            idoperacoes: apenasIds[index],
            label: resultado.descricao,
            faturamento: resultado.faturamento,
            tpag: resultado.tpag,
          }))
          .filter((opcao): opcao is TAutoCompleteOption => opcao !== null);
        setOpcoesFixas(todasAsOpcoes);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apenasIds]);

  useEffect(() => {
    // Filtra a lista fixa localmente pelo texto digitado — sem ida ao servidor, já que são só
    // as (poucas) operações de `apenasIds`, já carregadas acima.
    if (!apenasIds || apenasIds.length === 0) return;
    const buscaNormalizada = busca.trim().toLowerCase();
    setOpcoes(buscaNormalizada ? opcoesFixas.filter(opcao => opcao.label.toLowerCase().includes(buscaNormalizada)) : opcoesFixas);
  }, [busca, apenasIds, opcoesFixas]);

  useEffect(() => {
    if (apenasIds && apenasIds.length > 0) return;

    setIsLoading(true);

    debounce(() => {
      EstoqueOperacoesService.getAlllist(busca, selectedId, tipo)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            // alert(result.message);
          } else {
            setOpcoes(result.data.map(operacoes => ({ idoperacoes: operacoes.idoperacoes, label: operacoes.descricao, operacao: operacoes.descricao,  faturamento: operacoes.faturamento, tpag: operacoes.tpag})));
          }
        });
    });
  }, [busca, selectedId, tipo, apenasIds]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;
    const selectedOption = opcoes.find(opcao => opcao.idoperacoes === selectedId);
    if (!selectedOption) return null;
    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'
      disablePortal
      options={opcoes}
      loading={isLoading} 
      disabled={isExternalLoading || disabled}
      value={autoCompleteSelectedOption}
      // Só busca sozinho por `reason==='input'` — ver AutoCompleteMunicipio.tsx pro caso
      // completo do loop que isso evita (reset automático do MUI virando busca).
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setBusca(newValue);
      }}
      onChange={(_, newValue) => { setSelectedId(newValue?.idoperacoes); tpag?.(newValue?.tpag); faturamento?.(newValue?.faturamento); operacao?.(newValue?.label); onIdOperacaoChange?.(newValue?.idoperacoes); setBusca(''); clearError(); }}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label="Operações"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};